import { useEffect, useState } from "react";
import {
    FiSearch,
    FiLoader,
    FiPlus,
    FiMinus,
    FiClipboard,
} from "react-icons/fi";
import { useDebouncedCallback } from "use-debounce";
import { useAppDispatch, useAppSelector } from "../../store/hooks";
import { fetchCreditAuditLogs } from "../../store/smileDesign/paymentSlice";
import TablePagination from "../../components/smileDesign/TablePagination";

const formatDate = (value: string) => {
    const date = new Date(value);
    return date.toLocaleString();
};

const CreditAuditLogs = () => {
    const dispatch = useAppDispatch();
    const { creditAuditLogs, auditLogsPagination, auditLogsLoading, error } =
        useAppSelector((state) => state.payment);

    const [search, setSearch] = useState("");
    const [actionFilter, setActionFilter] = useState("");
    const [page, setPage] = useState(1);
    const limit = 20;

    const loadLogs = (overrides?: {
        page?: number;
        search?: string;
        action?: string;
    }) => {
        dispatch(
            fetchCreditAuditLogs({
                page: overrides?.page ?? page,
                limit,
                search: (overrides?.search ?? search) || undefined,
                action: (overrides?.action ?? actionFilter) || undefined,
            })
        );
    };

    const debouncedSearch = useDebouncedCallback((value: string) => {
        setPage(1);
        loadLogs({ page: 1, search: value });
    }, 400);

    useEffect(() => {
        loadLogs();
    }, []);

    const handleSearchChange = (value: string) => {
        setSearch(value);
        debouncedSearch(value);
    };

    const handleActionFilter = (value: string) => {
        setActionFilter(value);
        setPage(1);
        loadLogs({ page: 1, action: value });
    };

    return (
        <div className="min-h-screen bg-[#080808] text-white p-4 sm:p-6">
            <div className="w-full">
                <div className="mb-8">
                    <h1 className="text-3xl sm:text-4xl font-bold bg-gradient-to-r from-cyan-400 to-blue-500 bg-clip-text text-transparent">
                        Credit Audit Logs
                    </h1>
                    <p className="text-[#8b8f8d] mt-2 text-sm sm:text-base">
                        Track every manual credit add or remove — who did it,
                        when, and what changed.
                    </p>
                </div>

                <div className="flex flex-col lg:flex-row gap-4 mb-6">
                    <div className="relative flex-1">
                        <FiSearch className="absolute left-4 top-1/2 -translate-y-1/2 text-[#6b6f6d]" />
                        <input
                            type="text"
                            placeholder="Search admin, user, email, or remark..."
                            value={search}
                            onChange={(e) => handleSearchChange(e.target.value)}
                            className="w-full bg-[#101211] border border-[#1f2221] rounded-2xl py-3 pl-12 pr-4 text-white placeholder:text-[#6b6f6d] focus:outline-none focus:ring-2 focus:ring-cyan-500"
                        />
                    </div>

                    <select
                        value={actionFilter}
                        onChange={(e) => handleActionFilter(e.target.value)}
                        className="bg-[#101211] border border-[#1f2221] rounded-2xl py-3 px-4 text-white focus:outline-none focus:ring-2 focus:ring-cyan-500 lg:min-w-[180px]"
                    >
                        <option value="">All actions</option>
                        <option value="ADD">Added credits</option>
                        <option value="REMOVE">Removed credits</option>
                    </select>
                </div>

                {error && (
                    <div className="mb-6 rounded-2xl border border-red-500/20 bg-red-500/10 text-red-400 px-5 py-4">
                        {error}
                    </div>
                )}

                {auditLogsLoading && (
                    <div className="flex justify-center py-20">
                        <FiLoader className="animate-spin text-cyan-400 text-4xl" />
                    </div>
                )}

                {!auditLogsLoading && creditAuditLogs.length === 0 && (
                    <div className="text-center py-20 rounded-3xl border border-[#1f2221] bg-[#101211]">
                        <FiClipboard className="text-5xl text-[#3a3d3c] mx-auto mb-4" />
                        <h3 className="text-xl font-semibold">No audit logs found</h3>
                        <p className="text-[#8b8f8d] mt-2">
                            Manual credit changes will appear here.
                        </p>
                    </div>
                )}

                {!auditLogsLoading && creditAuditLogs.length > 0 && (
                    <>
                    <div className="hidden lg:block bg-[#101211] border border-[#1f2221] rounded-3xl overflow-hidden shadow-2xl">
                        <div className="overflow-x-auto">
                            <table className="w-full min-w-[960px]">
                                <thead className="bg-[#080808] border-b border-[#1f2221]">
                                    <tr>
                                        <th className="px-6 py-5 text-left text-[#b7bab8] font-semibold">
                                            When
                                        </th>
                                        <th className="px-6 py-5 text-left text-[#b7bab8] font-semibold">
                                            Admin
                                        </th>
                                        <th className="px-6 py-5 text-left text-[#b7bab8] font-semibold">
                                            User
                                        </th>
                                        <th className="px-6 py-5 text-left text-[#b7bab8] font-semibold">
                                            Action
                                        </th>
                                        <th className="px-6 py-5 text-left text-[#b7bab8] font-semibold">
                                            Credits
                                        </th>
                                        <th className="px-6 py-5 text-left text-[#b7bab8] font-semibold">
                                            Balance
                                        </th>
                                        <th className="px-6 py-5 text-left text-[#b7bab8] font-semibold">
                                            Remark
                                        </th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {creditAuditLogs.map((log) => (
                                        <tr
                                            key={log.log_id}
                                            className="border-b border-[#1f2221] hover:bg-[#151716] transition-all"
                                        >
                                            <td className="px-6 py-5 text-sm text-[#b7bab8] whitespace-nowrap">
                                                {formatDate(log.created_at)}
                                            </td>
                                            <td className="px-6 py-5">
                                                <div className="font-medium">
                                                    {log.admin_name}
                                                </div>
                                                <div className="text-xs text-[#8b8f8d]">
                                                    {log.admin_email}
                                                </div>
                                            </td>
                                            <td className="px-6 py-5">
                                                <div className="font-medium">
                                                    {log.target_user_name}
                                                </div>
                                                <div className="text-xs text-[#8b8f8d]">
                                                    {log.target_user_email}
                                                </div>
                                            </td>
                                            <td className="px-6 py-5">
                                                <span
                                                    className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-sm font-medium ${
                                                        log.action === "ADD"
                                                            ? "bg-green-500/10 border border-green-500/20 text-green-400"
                                                            : "bg-red-500/10 border border-red-500/20 text-red-400"
                                                    }`}
                                                >
                                                    {log.action === "ADD" ? (
                                                        <FiPlus />
                                                    ) : (
                                                        <FiMinus />
                                                    )}
                                                    {log.action === "ADD"
                                                        ? "Added"
                                                        : "Removed"}
                                                </span>
                                            </td>
                                            <td className="px-6 py-5 font-semibold text-cyan-400">
                                                {log.action === "ADD" ? "+" : "-"}
                                                {log.credits}
                                            </td>
                                            <td className="px-6 py-5 text-sm text-[#b7bab8] whitespace-nowrap">
                                                {log.balance_before} →{" "}
                                                <span className="text-white font-medium">
                                                    {log.balance_after}
                                                </span>
                                            </td>
                                            <td className="px-6 py-5 text-sm text-[#b7bab8] max-w-xs">
                                                {log.remark}
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>

                        <TablePagination
                            pagination={auditLogsPagination}
                            onPageChange={(nextPage) => {
                                setPage(nextPage);
                                loadLogs({ page: nextPage });
                            }}
                            itemLabel="audit logs"
                        />
                    </div>

                    {/* Mobile cards */}
                    <div className="lg:hidden space-y-4">
                        {creditAuditLogs.map((log) => (
                            <div
                                key={log.log_id}
                                className="bg-[#101211] border border-[#1f2221] rounded-2xl p-4"
                            >
                                <div className="flex items-start justify-between gap-3">
                                    <span
                                        className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium ${
                                            log.action === "ADD"
                                                ? "bg-green-500/10 border border-green-500/20 text-green-400"
                                                : "bg-red-500/10 border border-red-500/20 text-red-400"
                                        }`}
                                    >
                                        {log.action === "ADD" ? <FiPlus /> : <FiMinus />}
                                        {log.action === "ADD" ? "Added" : "Removed"}
                                    </span>
                                    <p className="text-xs text-[#8b8f8d] text-right shrink-0">
                                        {formatDate(log.created_at)}
                                    </p>
                                </div>
                                <p className="mt-3 text-lg font-semibold text-cyan-400">
                                    {log.action === "ADD" ? "+" : "-"}
                                    {log.credits} credits
                                </p>
                                <div className="mt-3 space-y-2 text-sm">
                                    <div>
                                        <p className="text-[#6b6f6d] text-xs">Admin</p>
                                        <p className="font-medium break-words">{log.admin_name}</p>
                                        <p className="text-xs text-[#8b8f8d] break-all">{log.admin_email}</p>
                                    </div>
                                    <div>
                                        <p className="text-[#6b6f6d] text-xs">User</p>
                                        <p className="font-medium break-words">{log.target_user_name}</p>
                                        <p className="text-xs text-[#8b8f8d] break-all">{log.target_user_email}</p>
                                    </div>
                                    <p className="text-[#b7bab8]">
                                        Balance: {log.balance_before} →{" "}
                                        <span className="text-white font-medium">{log.balance_after}</span>
                                    </p>
                                    {log.remark && (
                                        <p className="text-[#8b8f8d] break-words">{log.remark}</p>
                                    )}
                                </div>
                            </div>
                        ))}
                        <div className="bg-[#101211] border border-[#1f2221] rounded-2xl overflow-hidden">
                            <TablePagination
                                pagination={auditLogsPagination}
                                onPageChange={(nextPage) => {
                                    setPage(nextPage);
                                    loadLogs({ page: nextPage });
                                }}
                                itemLabel="audit logs"
                            />
                        </div>
                    </div>
                    </>
                )}
            </div>
        </div>
    );
};

export default CreditAuditLogs;
